import React, { useState, useEffect } from 'react';
import { getCmsGalleryByKey, getOptimizedImageUrl } from '../lib/cms';

export default function CmsGallery({ galleryKey, title = 'Billeder fra hverdagen', subtitle }) {
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(null);

  useEffect(() => {
    let isMounted = true;
    if (!galleryKey) {
      setLoading(false);
      return;
    }

    setLoading(true);
    getCmsGalleryByKey(galleryKey)
      .then((data) => {
        if (isMounted) {
          setGallery(data);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error('Failed to load CMS gallery:', err);
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [galleryKey]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (activeImageIndex === null) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setActiveImageIndex(null);
      } else if (e.key === 'ArrowLeft') {
        setActiveImageIndex((prev) => (prev > 0 ? prev - 1 : (gallery?.images?.length || 1) - 1));
      } else if (e.key === 'ArrowRight') {
        setActiveImageIndex((prev) => (prev < (gallery?.images?.length || 1) - 1 ? prev + 1 : 0));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    // Prevent scrolling behind modal
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [activeImageIndex, gallery]);

  if (loading) {
    return (
      <div className="py-12 flex justify-center items-center text-slate-400">
        <i className="fa-solid fa-spinner fa-spin text-2xl" aria-hidden="true"></i>
        <span className="ml-2 text-sm">Henter billedgalleri fra CMS...</span>
      </div>
    );
  }

  if (!gallery || !gallery.images || gallery.images.length === 0) {
    return null;
  }

  const images = gallery.images;

  return (
    <section className="my-12 sm:my-16">
      <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-10">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#f8f7f4] border border-[#eae6dd] text-xs font-semibold text-[#b39366] uppercase tracking-wider mb-2.5">
          <i className="fa-solid fa-images text-[11px]" aria-hidden="true"></i>
          <span>CMS Galleri</span>
        </div>
        <h3 className="text-2xl sm:text-3xl font-bold text-[#041c2c] tracking-tight">
          {gallery.title || title}
        </h3>
        {(gallery.description || subtitle) && (
          <p className="text-slate-600 text-sm sm:text-base mt-2">
            {gallery.description || subtitle}
          </p>
        )}
      </div>

      {/* Grid of gallery images */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {images.map((imgUrl, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => setActiveImageIndex(idx)}
            className="group relative aspect-4/3 rounded-xl overflow-hidden shadow-sm hover:shadow-md border border-slate-200/80 bg-slate-100 focus:outline-none focus:ring-2 focus:ring-[#b39366] cursor-pointer text-left block w-full transition-all duration-200"
            aria-label={`Åbn billede ${idx + 1} i fuld skærm`}
          >
            <img
              src={getOptimizedImageUrl(imgUrl, 600, 80)}
              alt={`${gallery.title || 'Galleri'} - billede ${idx + 1}`}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-[#041c2c]/0 group-hover:bg-[#041c2c]/30 transition-colors flex items-center justify-center">
              <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 text-[#041c2c] w-10 h-10 rounded-full flex items-center justify-center shadow-lg text-sm">
                <i className="fa-solid fa-magnifying-glass-plus" aria-hidden="true"></i>
              </span>
            </div>
          </button>
        ))}
      </div>

      {/* Touch-friendly Lightbox Modal */}
      {activeImageIndex !== null && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Billedvisning"
          className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-6 select-none"
          onClick={() => setActiveImageIndex(null)}
        >
          {/* Close Button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setActiveImageIndex(null);
            }}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 text-white/90 hover:text-white bg-black/60 hover:bg-black/90 p-3 rounded-full text-xl z-[10000] transition-colors cursor-pointer border border-white/10"
            aria-label="Luk visning"
          >
            <i className="fa-solid fa-xmark w-6 h-6 flex items-center justify-center" aria-hidden="true"></i>
          </button>

          {/* Prev Button */}
          {images.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setActiveImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
              }}
              className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 text-white/90 hover:text-white bg-black/60 hover:bg-black/90 p-3.5 rounded-full text-lg z-[10000] transition-colors cursor-pointer border border-white/10"
              aria-label="Forrige billede"
            >
              <i className="fa-solid fa-chevron-left w-5 h-5 flex items-center justify-center" aria-hidden="true"></i>
            </button>
          )}

          {/* Next Button */}
          {images.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setActiveImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
              }}
              className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 text-white/90 hover:text-white bg-black/60 hover:bg-black/90 p-3.5 rounded-full text-lg z-[10000] transition-colors cursor-pointer border border-white/10"
              aria-label="Næste billede"
            >
              <i className="fa-solid fa-chevron-right w-5 h-5 flex items-center justify-center" aria-hidden="true"></i>
            </button>
          )}

          {/* Main Image Container */}
          <div
            className="relative max-w-4xl w-full max-h-[85vh] flex flex-col items-center justify-center pointer-events-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={images[activeImageIndex]}
              alt={`${gallery.title || 'Galleri'} - stort billede ${activeImageIndex + 1}`}
              className="max-h-[75vh] w-auto max-w-full object-contain rounded-lg shadow-2xl border border-white/10"
            />
            <div className="mt-3 text-center text-slate-200 text-xs sm:text-sm font-medium bg-black/50 px-4 py-1.5 rounded-full border border-white/10">
              Billede {activeImageIndex + 1} af {images.length}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
